import { Check, ClipboardCheck, Download, RotateCcw, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Section } from '@/components/common/Blocks'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { promptModal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import { fmtDate, fmtDateTime } from '@/features/shared/format'
import { apiErrorMessage } from '@/i18n'
import { useAuth } from '@/lib/auth/AuthProvider'
import { DOC_REVIEW_PERM, downloadCaseFile, type ReviewDecision, type ReviewItem, useReviewCase, useReviewQueue } from './followupApi'

/** İnceleme bekleyen personel belgeleri: dosyayı indir, onayla, düzeltme iste ya da reddet. */
export function BelgeTakipPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const canReview = can(DOC_REVIEW_PERM)
  const queue = useReviewQueue(canReview)
  const review = useReviewCase()
  const fail = (e: unknown) => toast.error(apiErrorMessage(e))

  const decide = async (it: ReviewItem, decision: ReviewDecision) => {
    let reason: string | undefined
    if (decision !== 'approved') {
      const v = await promptModal({ title: t(`hr.belgeler.review.${decision}Title`), placeholder: t('hr.belgeler.review.reasonPlaceholder'), confirmLabel: t(`hr.belgeler.review.${decision}`) })
      if (v == null) return
      if (!v.trim()) {
        toast.error(t('hr.belgeler.review.reasonRequired'))
        return
      }
      reason = v.trim()
    }
    review.mutate({ caseId: it.caseId, employeeId: it.employeeId, decision, reason }, { onSuccess: () => toast.success(t(`hr.belgeler.review.${decision}Done`)), onError: fail })
  }

  return (
    <>
      <PageHeader eyebrow={t('nav.groups.hr')} title={t('hr.belgeler.takip.title')} description={t('hr.belgeler.takip.subtitle')} />
      <Section title={t('hr.belgeler.takip.queue')} icon={ClipboardCheck} bodyClassName="p-0">
        {!canReview ? (
          <EmptyState icon={ClipboardCheck} title={t('hr.belgeler.takip.noPermission')} />
        ) : queue.isError ? (
          <ErrorState error={queue.error} onRetry={() => void queue.refetch()} />
        ) : queue.isLoading ? (
          <Skeleton className="m-4 h-40" />
        ) : !queue.data?.length ? (
          <EmptyState icon={ClipboardCheck} title={t('hr.belgeler.takip.empty')} />
        ) : (
          <ul className="divide-y">
            {queue.data.map((it) => (
              <li key={it.caseId} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1 basis-60">
                  <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                    {it.title}
                    {it.revision > 1 ? <Badge tone="warning">{t('hr.belgeler.takip.revision', { n: it.revision })}</Badge> : null}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <Link to={`/hr/personel/${it.employeeId}?sekme=belge`} className="hover:text-primary hover:underline">
                      {it.employeeName}
                    </Link>
                    {' · '}
                    {t('hr.belgeler.takip.submitted', { at: fmtDateTime(it.submittedAt) })}
                    {it.dueAt ? ` · ${t('hr.belgeler.takip.due', { date: fmtDate(it.dueAt) })}` : ''}
                  </p>
                  {it.note ? <p className="text-xs text-muted-foreground">{it.note}</p> : null}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="ghost" onClick={() => void downloadCaseFile(it.caseId, it.title).catch(fail)}>
                    <Download />
                    {t('hr.belgeler.review.download')}
                  </Button>
                  <Button size="sm" onClick={() => void decide(it, 'approved')} disabled={review.isPending}>
                    <Check />
                    {t('hr.belgeler.review.approved')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => void decide(it, 'correction_requested')} disabled={review.isPending}>
                    <RotateCcw />
                    {t('hr.belgeler.review.correction_requested')}
                  </Button>
                  <Button size="sm" variant="cancel" onClick={() => void decide(it, 'rejected')} disabled={review.isPending}>
                    <X />
                    {t('hr.belgeler.review.rejected')}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </>
  )
}
